/**
 * JourneyRail — always-visible pillar progress strip, rendered as a
 * proper London-Tube-style map with real lines, curves, interchange
 * rings and terminus dots.
 *
 *                      · Employer (cyan)
 *                       \
 *   ●═══════●═══════○═══════○     (main purple line)
 *   Pick    Place    Prove    Apply
 *    | \
 *    Industry (orange), Earnings (amber)   → all merge INTO Pick
 *              City (teal)   → into Place
 *              Rankings (rose) → into Apply
 *
 * Design goals:
 * • Thick coloured line — the "central line" — passes through all
 *   four main stations. The completed section is painted in WUA
 *   purple; the pending section is muted stone.
 * • Feeder tracks are drawn as CURVES (quadratic Bezier) that peel
 *   off the main line and terminate at their own coloured terminus
 *   dot with an inline label, exactly like a Tube branch.
 * • Each main station is a proper interchange ring — white fill with
 *   a thick purple stroke. Completed shows a white check; current
 *   station gets a pulsing outer ring; upcoming shows the step
 *   number.
 * • Sticky under the Navbar on desktop. Compact horizontal on mobile
 *   (mobile keeps the previous minimal design — a proper tube map is
 *   unreadable at 320px).
 * • Every visual station and every feeder terminus is a hit-testable
 *   link (rendered as an absolute-positioned overlay Anchor so the
 *   SVG stays declarative and screen-readers get real semantics).
 */
import { Link, useLocation } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { Cloud, Check, Info } from "lucide-react";
import { useJourney } from "@/context/JourneyContext";
import { useAuth } from "@/context/AuthContext";

const STATIONS = [
  { key: "pick",  label: "Pick",  full: "Pick — your course interests", to: "/beta/course-fit", progress: "pick" },
  { key: "place", label: "Place", full: "Place — city & uni, alongside your course", to: "/cities/matcher", progress: "place"  },
  // progress: which JourneyContext entry drives the dot. Course reads the
  // stored "prove" artefact (prove.chances_confirmed is only written by
  // Quick Check), so it isn't a reinterpretation. Advanced Search has no
  // completion artefact, so Prove only ever shows "you are here".
  { key: "course", label: "Course", full: "Course — Quick Check: can I get in?",  to: "/admission", progress: "prove" },
  // Prove is an assessment you can run any time between Course and Shortlist (dashed ring).
  { key: "prove", label: "Prove", full: "Prove — assessment step: Advanced Search, where I stand", to: "/advanced", progress: "advanced", assessment: true },
  // Shortlist reads the stored "apply" artefact (how.shortlist_confirmed, from confirming the shortlist).
  { key: "shortlist", label: "Shortlist", full: "Shortlist — my final list", to: "/shortlist", progress: "apply" },
  // Apply has no completion artefact yet, so it only ever shows "you are here".
  { key: "apply", label: "Apply", full: "Apply — your applications for the shortlist", to: "/applications", progress: "applications" },
];

// Feeder lines — alternative entry points that merge INTO the central
// Pick/Place/Prove/Apply line. Each has its own Tube-style hue, a
// horizontal offset (`dx`) from its host station so multiple feeders
// on the same stop fan out cleanly, and a one-line `blurb` shown in
// the hover popover so students understand what each entry unlocks.
const FEEDER_LINES = [
  // Pick — what to study (listed west → east, which is also keyboard order)
  { key: "employer", label: "Employer", colour: "#0891B2", into: "pick",  to: "/employers",       dx: -70,
    blurb: "Reverse-engineer the degree that feeds your dream firm — start with the employer, land on the courses that get you hired." },
  { key: "rankings", label: "Rankings", colour: "#E11D48", into: "pick",  to: "/rankings",        dx:   0,
    blurb: "Subject-by-country league tables built on graduate salary, value for money and satisfaction — a way into what to study." },
  { key: "earnings", label: "Earnings", colour: "#F59E0B", into: "pick",  to: "/highest-earning", dx: +70,
    blurb: "Jump straight to the UK courses whose grads earn £45k+ three years out — sorted by real HESA salary data." },
  // Subject guides join twice: before you pick, and while you check a course.
  { key: "subjects", label: "Subjects", colour: "#175D51", into: "pick",  to: "/subjects",        dx: +150,
    blurb: "Read the subject guides — what you actually study, whether you'll enjoy it, and where it leads — before you pick." },
  // Place — where to live
  { key: "city",     label: "City",     colour: "#14B8A6", into: "place", to: "/cities",          dx: -25,
    blurb: "Skip the matcher and browse 338 editorial student-city guides — cost, vibe, transport, safety." },
  { key: "industry", label: "Industry", colour: "#F97316", into: "place", to: "/cities/hubs",     dx: +50,
    blurb: "Browse 18 ranked lists of cities by sector strength — Finance, Biotech, Aerospace, Gaming & more — then open the city guides." },
  // How applying works in each country — part of choosing where to study
  { key: "country-guides", label: "Country guides", colour: "#2563EB", into: "place", to: "/apply", dx: +140,
    blurb: "Applying abroad? Start with the country, not the course — how applications, deadlines and tests work in each country." },
  // Reflective loop
  // Leaves Course, rises into Prove (spur) and rejoins at Shortlist; it also reads Pick and Place answers.
  { key: "realisations", label: "Realisations", colour: "#A21CAF", into: "course", also: "shortlist", spur: "prove", to: "/realisations", dx: 0,
    blurb: "Look back at what your choices say about you. It reads your Pick and Place answers, and what it notices feeds into Course, Prove and your Shortlist." },
  // Course — the same subject guides, for checking a course you're considering
  { key: "subjects-course", label: "Subjects", colour: "#175D51", into: "course", to: "/subjects", dx: -55,
    blurb: "Checking a course? Its subject guide shows the teaching, the assessment and the careers it leads to." },
];

const stationLabel = (k) => STATIONS.find((s) => s.key === k)?.label;
const feederLabel = (k) => FEEDER_LINES.find((x) => x.key === k)?.label;
// Terminus x of a feeder; a two-stop feeder sits midway between its stops.
const feederX = (f) => (f.spur ? STATION_X[f.spur] : f.also ? (STATION_X[f.into] + STATION_X[f.also]) / 2 : STATION_X[f.into] + f.dx);
const feederStops = (f) => [f.into, f.spur, f.also].filter(Boolean).map(stationLabel);
const feederFooter = (f) =>
  f.spur ? `Feeds into ${feederStops(f).slice(0, -1).join(", ")} and ${feederStops(f).slice(-1)}; reads your Pick and Place answers`
  : f.also ? `Feeds into ${stationLabel(f.into)} and ${stationLabel(f.also)}`
    : f.via ? `Connects to ${feederLabel(f.via)}, which merges into ${stationLabel(f.into)}`
      : `Merges into ${stationLabel(f.into)}`;

// Paths where the rail should NOT render.
const HIDE_ON = [
  "/admin", "/uni-portal", "/firm-portal", "/counsellors",
  "/auth/callback", "/legal", "/claim", "/plus", "/wua-plus",
];

// Central line palette (WUA brand purple).
const LINE_COMPLETED = "#7C3AED";
const LINE_PENDING   = "#E7E5E4"; // stone-200

// SVG canvas geometry.
const W = 1000;
const H = 140;
const MAIN_Y = 44;
const BRANCH_Y = 104;
const STATION_R = 13;
const STATION_INNER_R = 9;
const FEEDER_R = 6;
const LINE_WEIGHT = 6;

// Station x-positions — room for Pick's three feeders and Place's two
// to fan west/east, and for the Realisations loop under Prove.
const STATION_X = { pick: 100, place: 340, course: 660, prove: 770, shortlist: 875, apply: 965 };

/* ------------------------------------------------------------------ */
/*  Small SVG check-mark, centred on (0,0)                            */
/* ------------------------------------------------------------------ */
function CheckPath({ colour = "white" }) {
  return (
    <path
      d="M -4 0 L -1.5 2.5 L 4 -3"
      fill="none"
      stroke={colour}
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Feeder branch — curved track + terminus dot + label                */
/* ------------------------------------------------------------------ */
function FeederBranch({ feeder, isActive }) {
  const hostX = STATION_X[feeder.into];
  const via = feeder.via && FEEDER_LINES.find((x) => x.key === feeder.via);
  // A `via` line starts at its parent feeder's terminus (an interchange).
  const startX = via ? hostX + via.dx + FEEDER_R : hostX;
  const startY = via ? BRANCH_Y : MAIN_Y;
  const endX = feederX(feeder);
  const endY = BRANCH_Y;
  // Straight vertical for centred feeders, quadratic Bezier that
  // eases off the main line for offset feeders — mimics real Tube
  // branch curvature.
  // Reflective loops leave and rejoin the line at the same stop.
  const alsoX = feeder.also && STATION_X[feeder.also];
  const d = feeder.also
    ? `M ${startX} ${startY} Q ${startX} ${endY}, ${endX} ${endY} Q ${alsoX} ${endY}, ${alsoX} ${startY}` +
      (feeder.spur ? ` M ${endX} ${endY} L ${endX} ${startY + STATION_R}` : "")
    : feeder.loop
    ? `M ${startX} ${startY} C ${startX - 46} 124, ${startX + 46} 124, ${startX} ${startY}`
    : via
      ? `M ${startX} ${startY} L ${endX} ${endY}`
    : feeder.dx === 0
      ? `M ${startX} ${startY} L ${endX} ${endY}`
      : `M ${startX} ${startY} Q ${startX} ${startY + 32}, ${endX} ${endY}`;
  return (
    <g>
      <path
        d={d}
        stroke={feeder.colour}
        strokeWidth={LINE_WEIGHT - 1}
        fill="none"
        strokeLinecap="round"
      />
      {/* Terminus dot — filled colour, white halo, active feeder gets
          a larger outer ring so students can see "you are on this
          branch". */}
      {isActive && (
        <circle
          cx={endX}
          cy={endY}
          r={FEEDER_R + 4}
          fill="none"
          stroke={feeder.colour}
          strokeOpacity={0.35}
          strokeWidth={3}
        >
          <animate
            attributeName="r"
            values={`${FEEDER_R + 3};${FEEDER_R + 7};${FEEDER_R + 3}`}
            dur="1.6s"
            repeatCount="indefinite"
          />
        </circle>
      )}
      <circle
        cx={endX}
        cy={endY}
        r={FEEDER_R}
        fill={feeder.colour}
        stroke="white"
        strokeWidth={2}
      />
      {/* Feeder label — sits directly below its terminus. */}
      <text
        x={endX}
        y={endY + 20}
        textAnchor="middle"
        fontSize={12}
        fontWeight={500}
        letterSpacing={0.6}
        fill="#44403C"
        className="font-underground-sc"
      >
        {feeder.label}
      </text>
    </g>
  );
}

/* ------------------------------------------------------------------ */
/*  Main-line station (interchange ring)                               */
/* ------------------------------------------------------------------ */
function MainStation({ station, index, state }) {
  const x = STATION_X[station.key];
  const y = MAIN_Y;
  const isCurrent   = !!state?.current;
  const isCompleted = !!state?.completed;

  const stroke = isCompleted || isCurrent ? LINE_COMPLETED : "#A8A29E";
  const fill   = isCompleted ? LINE_COMPLETED : "white";

  return (
    <g>
      {/* Pulsing halo for the current stop */}
      {isCurrent && (
        <circle cx={x} cy={y} r={STATION_R + 4} fill="none" stroke={LINE_COMPLETED} strokeOpacity={0.35} strokeWidth={3}>
          <animate
            attributeName="r"
            values={`${STATION_R + 2};${STATION_R + 8};${STATION_R + 2}`}
            dur="1.6s"
            repeatCount="indefinite"
          />
        </circle>
      )}
      {/* Outer interchange ring */}
      <circle cx={x} cy={y} r={STATION_R} fill={fill} stroke={stroke} strokeWidth={3}
        strokeDasharray={station.assessment && !isCompleted ? "5 3" : undefined} data-testid={station.assessment ? "journey-rail-assessment-ring" : undefined} />
      {/* Inner white plate (only for pending/current — completed keeps
          the filled purple look). */}
      {!isCompleted && (
        <circle cx={x} cy={y} r={STATION_INNER_R} fill="white" />
      )}
      {/* Content: check, current-dot, or step number */}
      {isCompleted ? (
        <g transform={`translate(${x} ${y})`}>
          <CheckPath colour="white" />
        </g>
      ) : isCurrent ? (
        <circle cx={x} cy={y} r={4} fill={LINE_COMPLETED} />
      ) : (
        <text
          x={x}
          y={y + 4}
          textAnchor="middle"
          fontSize={11}
          fontWeight={700}
          fill="#78716C"
          fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
        >
          {index + 1}
        </text>
      )}
      {/* Station name — above the main line, small-caps Tube face */}
      <text
        x={x}
        y={y - 24}
        textAnchor="middle"
        fontSize={13}
        fontWeight={600}
        letterSpacing={0.8}
        fill={isCurrent ? LINE_COMPLETED : isCompleted ? "#1C1917" : "#78716C"}
        className="font-underground-sc"
      >
        {station.label}
      </text>
    </g>
  );
}

/* ------------------------------------------------------------------ */
/*  Mobile compact station (unchanged design)                          */
/* ------------------------------------------------------------------ */
function MobileStation({ station, index, state }) {
  const isCurrent   = !!state?.current;
  const isCompleted = !!state?.completed;
  let nodeClass = "bg-white text-stone-500 border border-stone-300";
  if (isCompleted) nodeClass = "bg-[#7C3AED] text-white border-[#7C3AED]";
  else if (isCurrent) nodeClass = "bg-white text-[#7C3AED] border-2 border-[#7C3AED]";
  return (
    <Link
      to={station.to}
      data-testid={`journey-station-${station.key}`}
      aria-label={`${station.full} — ${isCompleted ? "completed" : isCurrent ? "current" : "upcoming"}`}
      className="group relative shrink-0"
    >
      <span className={`flex items-center justify-center rounded-full h-7 w-7 ${nodeClass} ${isCurrent ? "animate-pulse" : ""}`}>
        {isCompleted ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : <span className="text-[10px] font-mono font-semibold">{index + 1}</span>}
      </span>
      <span className={`absolute left-1/2 -translate-x-1/2 top-full mt-1.5 whitespace-nowrap text-[11px] font-underground-sc tracking-wider pointer-events-none ${isCurrent ? "text-[#7C3AED]" : isCompleted ? "text-stone-800" : "text-stone-500"}`}>
        {station.label}
      </span>
    </Link>
  );
}

/* Feeder hit area + explainer. Opens below the dot (the rail sits under
   the sticky header, so there's no room above), flips above only when
   below is short, and is clamped inside the viewport horizontally. */
const TIP_W = 256;
const TIP_GAP = 12;
function FeederHit({ f }) {
  const ref = useRef(null);
  const [tip, setTip] = useState(null);
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const est = 132;
    const below = window.innerHeight - r.bottom - 22;
    const header = document.querySelector("header")?.getBoundingClientRect().bottom || 0;
    const side = below >= est + TIP_GAP || below >= r.top - header ? "below" : "above";
    const cx = r.left + r.width / 2;
    const left = Math.min(Math.max(cx - TIP_W / 2, 8), window.innerWidth - TIP_W - 8);
    setTip({ side, shift: left - (cx - TIP_W / 2) });
  };
  const hide = () => setTip(null);
  useEffect(() => {
    if (!tip) return undefined;
    const onKey = (e) => { if (e.key === "Escape") hide(); };
    const onScroll = () => hide();
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("scroll", onScroll); };
  }, [tip]);
  const cxPct = (feederX(f) / W) * 100;
  const rPct = ((FEEDER_R + 3) / W) * 100;
  const tipId = `journey-feeder-tip-${f.key}`;
  return (
    <div
      ref={ref}
      className="absolute pointer-events-auto"
      style={{ left: `${cxPct}%`, top: `${(BRANCH_Y / H) * 100}%`, width: `max(${rPct * 2}%, 28px)`, aspectRatio: "1 / 1", transform: "translate(-50%, -50%)" }}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <Link
        to={f.to}
        data-testid={`journey-feeder-${f.key}`}
        aria-label={`${f.also ? "Reflective line" : "Alternative starting line"}: ${f.label} — ${f.blurb} ${feederFooter(f)}.`}
        aria-describedby={tip ? tipId : undefined}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => {
          // Touch: first tap explains, second tap follows the line.
          if (e.nativeEvent?.pointerType === "touch" && !tip) { e.preventDefault(); show(); }
        }}
        className="block h-full w-full rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
        style={{ ["--tw-ring-color"]: f.colour }}
      />
      {tip && (
        <div
          id={tipId}
          role="tooltip"
          data-testid={`journey-feeder-tooltip-${f.key}`}
          data-side={tip.side}
          className={`pointer-events-none absolute left-1/2 z-40 ${tip.side === "below" ? "top-full mt-6" : "bottom-full mb-3"}`}
          style={{ width: TIP_W, transform: `translateX(calc(-50% + ${tip.shift}px))` }}
        >
          {tip.side === "below" && (
            <div className="mx-auto h-0 w-0 border-x-[7px] border-x-transparent border-b-[7px]" style={{ borderBottomColor: f.colour, transform: `translateX(${-tip.shift}px)` }} />
          )}
          <div className="rounded-lg bg-white shadow-lg border border-stone-200 overflow-hidden">
            <div className="h-1" style={{ backgroundColor: f.colour }} />
            <div className="px-3 py-2.5">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold" style={{ color: f.colour }}>
                <span aria-hidden className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: f.colour }} />
                {f.label} line
              </div>
              <div className="mt-1 text-[12px] leading-snug text-stone-700">{f.blurb}</div>
              <div className="mt-1.5 text-[10px] text-stone-500" data-testid={`journey-feeder-footer-${f.key}`}>{feederFooter(f)}</div>
            </div>
          </div>
          {tip.side === "above" && (
            <div className="mx-auto h-0 w-0 border-x-[6px] border-x-transparent border-t-[6px] border-t-white" style={{ transform: `translateX(${-tip.shift}px)` }} />
          )}
        </div>
      )}
    </div>
  );
}

// Persistent metro-roundel "i": always visible, no hover or animation needed.
function RouteFinderIcon({ testId }) {
  return (
    <span aria-hidden="true" data-testid={testId}
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-[3px] border-[#7C3AED] bg-white text-[#4C1D95] shadow-sm">
      <Info className="h-4 w-4" strokeWidth={2.5} />
    </span>
  );
}

/* Mobile: the same lines, grouped by the stop they join, as plain links. */
function MobileFeeders({ onPick }) {
  return (
    <div className="mt-8 space-y-3" data-testid="journey-mobile-lines">
      {[
        ...STATIONS.map((s) => ({ id: s.key, title: `Lines into ${s.label}`, lines: FEEDER_LINES.filter((f) => f.into === s.key && !f.also && !f.via) })),
        ...FEEDER_LINES.filter((f) => f.also).map((f) => ({ id: `${f.into}-${f.also}`, title: f.spur ? `Lines into ${feederStops(f).join(", ")} (reads Pick and Place)` : `Lines into ${stationLabel(f.into)} and ${stationLabel(f.also)}`, lines: [f] })),
      ].map(({ id, title, lines }) => {
        if (!lines.length) return null;
        return (
          <div key={id} data-testid={`journey-mobile-lines-${id}`}>
            <div className="text-[10px] uppercase tracking-[0.14em] text-stone-500">{title}</div>
            <ul className="mt-1 space-y-1">
              {lines.flatMap((f) => [f, ...FEEDER_LINES.filter((c) => c.via === f.key)]).map((f) => (
                <li key={f.key} className={f.via ? "ml-5 border-l-2 pl-2" : ""} style={f.via ? { borderColor: FEEDER_LINES.find((x) => x.key === f.via)?.colour } : undefined}>
                  <Link to={f.to} onClick={onPick} data-testid={`journey-mobile-feeder-${f.key}`}
                    className="flex min-h-[44px] items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]">
                    <span aria-hidden className="mt-1.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: f.colour }} />
                    <span className="min-w-0">
                      <span className="block text-[13px] font-medium text-stone-800">{f.label}{f.via && <span className="font-normal text-stone-500"> · via {feederLabel(f.via)}</span>}</span>
                      <span className="block text-[12px] leading-snug text-stone-600">{f.blurb}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function MobileSegment({ completed }) {
  return (
    <span
      aria-hidden="true"
      className="flex-1 h-[3px] rounded-full mx-1"
      style={{ backgroundColor: completed ? LINE_COMPLETED : LINE_PENDING }}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Main rail                                                          */
/* ------------------------------------------------------------------ */
export default function JourneyRail() {
  const location = useLocation();
  const { pillarProgress } = useJourney();
  const { user, login } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const hidden = useMemo(
    () => HIDE_ON.some((p) => (location.pathname || "").startsWith(p)),
    [location.pathname]
  );

  // Active route, not key: feeders sharing a route (both Subjects lines) light up together.
  const activeFeederTo = useMemo(() => {
    const path = location.pathname || "";
    // Whole path segments only (/applications is not /apply); the longest match wins (/cities/hubs over /cities).
    const hits = FEEDER_LINES.filter((f) => path === f.to || path.startsWith(`${f.to}/`));
    return hits.sort((a, b) => b.to.length - a.to.length)[0]?.to || null;
  }, [location.pathname]);

  // Track which segments are completed AS OF THE PREVIOUS render so
  // we can identify the moment a pillar flips completed — that
  // segment gets the paint-in animation while already-completed
  // segments stay static (no re-runs on every navigation).
  const prevCompletedRef = useRef(new Set());
  const completedSet = useMemo(() => {
    const set = new Set();
    for (let i = 0; i < STATIONS.length - 1; i++) {
      const s = STATIONS[i];
      const next = STATIONS[i + 1];
      const state = pillarProgress?.[s.progress] || {};
      const nextState = pillarProgress?.[next.progress] || {};
      if (state.completed || nextState.completed || nextState.current) {
        set.add(s.key);
      }
    }
    return set;
  }, [pillarProgress]);
  const freshCompleted = useMemo(() => {
    const fresh = new Set();
    for (const k of completedSet) if (!prevCompletedRef.current.has(k)) fresh.add(k);
    return fresh;
  }, [completedSet]);
  // Commit the current set as the next render's baseline. Done in
  // useMemo (post-render) so the animation runs once, then the
  // segment is treated as "old" next time.
  useMemo(() => { prevCompletedRef.current = completedSet; }, [completedSet]);

  // Broadcast pillar-level completions so downstream widgets (e.g.
  // PersonaCard) can reinforce the "you just unlocked X" moment
  // without wiring context or props. Fires exactly once per pillar
  // as it transitions to completed=true.
  const prevPillarSetRef = useRef(new Set());
  useEffect(() => {
    const cur = new Set(
      STATIONS.filter((s) => pillarProgress?.[s.progress]?.completed).map((s) => s.key)
    );
    for (const k of cur) {
      if (!prevPillarSetRef.current.has(k)) {
        window.dispatchEvent(
          new CustomEvent("wua:journey:pillar-completed", { detail: { pillar: k } })
        );
      }
    }
    prevPillarSetRef.current = cur;
  }, [pillarProgress]);

  if (hidden) return null;

  const completedCount = STATIONS.filter((s) => pillarProgress?.[s.progress]?.completed).length;
  const current = STATIONS.find((s) => s.progress === pillarProgress?.currentPillar)?.key || null;
  const userInitial = (user?.name || user?.email || "")[0]?.toUpperCase() || "";

  return (
    <>
      {/* ------------------------------------------------------------ */}
      {/*  Desktop / tablet tube map                                    */}
      {/* ------------------------------------------------------------ */}
      <div
        className="hidden md:block sticky top-[64px] z-30 bg-[#FAF7F2]/95 backdrop-blur border-b border-stone-200"
        data-testid="journey-rail"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex items-center gap-6">
          {/* Route Finder — opens the full /journey overview and navigation guide */}
          <Link
            to="/journey"
            data-testid="journey-rail-title-link"
            aria-label="Route Finder: How to Navigate this Site. Opens your journey and navigation guide"
            className="shrink-0 min-h-[44px] inline-flex items-center gap-2.5 leading-tight group rounded-lg pr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FAF7F2]"
          >
            <RouteFinderIcon testId="journey-rail-route-finder-icon" />
            <span>
              <span className="block text-[11px] tracking-[0.18em] text-stone-700 font-underground-sc group-hover:text-[#7C3AED] transition-colors" data-testid="journey-rail-route-finder-title">
                Route Finder
              </span>
              <span className="block text-[10px] text-stone-500 mt-0.5 font-underground-sc tracking-wider group-hover:text-[#7C3AED]/80 transition-colors" data-testid="journey-rail-route-finder-subtitle">
                How to Navigate this Site
              </span>
            </span>
          </Link>

          {/* The tube map itself */}
          <div className="relative flex-1 min-w-0" role="group" aria-label="Journey progress">
            <svg
              viewBox={`0 0 ${W} ${H}`}
              preserveAspectRatio="xMidYMid meet"
              className="w-full h-auto block"
              data-testid="journey-rail-svg"
            >
              {/* Pending main line — full length underneath */}
              <line
                x1={STATION_X.pick}
                y1={MAIN_Y}
                x2={STATION_X.apply}
                y2={MAIN_Y}
                stroke={LINE_PENDING}
                strokeWidth={LINE_WEIGHT}
                strokeLinecap="round"
              />

              {/* Inline style tag — one keyframe used by any segment
                  that has just flipped completed. Kept scoped to the
                  rail so it doesn't leak globally. */}
              <style>{`
                @keyframes journey-rail-paint-in {
                  from { stroke-dashoffset: var(--dash-len); }
                  to   { stroke-dashoffset: 0; }
                }
                .journey-rail-paint-in {
                  stroke-dasharray: var(--dash-len);
                  stroke-dashoffset: 0;
                  animation: journey-rail-paint-in 0.9s cubic-bezier(.2,.7,.2,1) forwards;
                }
              `}</style>

              {/* Completed segments, painted on top */}
              {STATIONS.slice(0, -1).map((s, i) => {
                const next = STATIONS[i + 1];
                if (!completedSet.has(s.key)) return null;
                const isFresh = freshCompleted.has(s.key);
                const dashLen = Math.abs(STATION_X[next.key] - STATION_X[s.key]);
                return (
                  <line
                    key={`seg-${s.key}`}
                    x1={STATION_X[s.key]}
                    y1={MAIN_Y}
                    x2={STATION_X[next.key]}
                    y2={MAIN_Y}
                    stroke={LINE_COMPLETED}
                    strokeWidth={LINE_WEIGHT}
                    strokeLinecap="round"
                    className={isFresh ? "journey-rail-paint-in" : undefined}
                    style={isFresh ? { ["--dash-len"]: `${dashLen}` } : undefined}
                  />
                );
              })}

              {/* Feeder branches (drawn UNDER the main stations so the
                  interchange rings sit on top of the branch entry) */}
              {FEEDER_LINES.map((f) => (
                <FeederBranch key={f.key} feeder={f} isActive={activeFeederTo === f.to} />
              ))}

              {/* Main-line interchange stations, drawn last */}
              {STATIONS.map((s, i) => (
                <MainStation
                  key={s.key}
                  station={s}
                  index={i}
                  state={pillarProgress?.[s.progress] || {}}
                />
              ))}
            </svg>

            {/* Absolute-positioned invisible Link overlays so each dot
                is a real anchor (keyboard + screen-reader friendly). */}
            <div className="absolute inset-0 pointer-events-none">
              {STATIONS.map((s) => {
                const cxPct = (STATION_X[s.key] / W) * 100;
                const cyPct = (MAIN_Y / H) * 100;
                const rPct  = ((STATION_R + 4) / W) * 100;
                return (
                  <Link
                    key={`hit-${s.key}`}
                    to={s.to}
                    data-testid={`journey-station-${s.key}`}
                    aria-label={`${s.full}${pillarProgress?.[s.progress]?.completed ? " (completed)" : ""}`}
                    aria-current={pillarProgress?.[s.progress]?.current ? "step" : undefined}
                    title={s.full}
                    className="absolute pointer-events-auto rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]"
                    style={{
                      left: `${cxPct}%`,
                      top: `${cyPct}%`,
                      width: `${rPct * 2}%`,
                      aspectRatio: "1 / 1",
                      transform: "translate(-50%, -50%)",
                    }}
                  />
                );
              })}
              {FEEDER_LINES.map((f) => <FeederHit key={`hit-${f.key}`} f={f} />)}
            </div>
          </div>

          {/* Right chip */}
          {user ? (
            <span
              data-testid="journey-auth-chip-signed-in"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] bg-emerald-50 text-emerald-800 border border-emerald-200 shrink-0"
            >
              <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-emerald-500 text-white text-[9px] font-bold">
                {userInitial || <Check className="h-2.5 w-2.5" />}
              </span>
              Synced
            </span>
          ) : (
            <button
              type="button"
              onClick={login}
              data-testid="journey-auth-chip-signed-out"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] bg-white text-stone-700 border border-stone-300 hover:border-[#002147] hover:bg-[#002147] hover:text-white transition-colors shrink-0"
              title="Sign in to sync your journey across devices"
            >
              <Cloud className="h-3 w-3" />
              Save my progress
            </button>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------ */}
      {/*  Mobile compact tube                                          */}
      {/* ------------------------------------------------------------ */}
      <div
        className="md:hidden sticky top-[64px] z-30 bg-[#FAF7F2]/95 backdrop-blur border-b border-stone-200"
        data-testid="journey-rail-mobile"
      >
        <div className="px-4 py-1.5 flex items-center gap-2">
          <Link
            to="/journey"
            data-testid="journey-rail-mobile-route-finder"
            aria-label="Route Finder: How to Navigate this Site. Opens your journey and navigation guide"
            title="Route Finder: How to Navigate this Site"
            className="shrink-0 inline-flex h-11 w-11 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]"
          >
            <RouteFinderIcon testId="journey-rail-mobile-route-finder-icon" />
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="journey-rail-mobile-toggle"
            className="flex-1 inline-flex items-center justify-between gap-2 px-3 py-1 rounded-full text-[11px] bg-white border border-stone-200 text-stone-700"
          >
            <span className="inline-flex items-center gap-1.5">
              <span className="font-mono text-[10px] text-[#7C3AED]">{completedCount}/{STATIONS.length}</span>
              <span className="font-medium">
                {current ? STATIONS.find((s) => s.key === current)?.full : "Your journey"}
              </span>
            </span>
            <span className="text-stone-400 text-[10px]">
              {mobileOpen ? "Hide" : "Show"}
            </span>
          </button>
          {!user && (
            <button
              type="button"
              onClick={login}
              data-testid="journey-auth-chip-mobile"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] bg-[#002147] text-white shrink-0"
            >
              <Cloud className="h-3 w-3" /> Save
            </button>
          )}
        </div>

        {mobileOpen && (
          <div className="px-4 pb-6 pt-1">
            <div className="flex items-center">
              {STATIONS.map((s, i) => {
                const state = pillarProgress?.[s.progress] || {};
                const nextState = pillarProgress?.[STATIONS[i + 1]?.progress] || {};
                const segmentCompleted = !!(state.completed || nextState.completed || nextState.current);
                return (
                  <div
                    key={s.key}
                    className={[
                      "flex items-center min-w-0",
                      i < STATIONS.length - 1 ? "flex-1" : "",
                    ].join(" ")}
                    onClick={() => setMobileOpen(false)}
                  >
                    <MobileStation station={s} index={i} state={state} />
                    {i < STATIONS.length - 1 && (
                      <MobileSegment completed={segmentCompleted} />
                    )}
                  </div>
                );
              })}
            </div>
            <MobileFeeders onPick={() => setMobileOpen(false)} />
          </div>
        )}
      </div>
    </>
  );
}
